import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import SyncAltOutlined from '@mui/icons-material/SyncAltOutlined';
import { SyncLog } from '../services/dashboardService';
import NoDataInfo from './NoDataInfo';
import { cellStyles, headerStyles } from './OffersList';
import StatusChip from '@shared/components/StatusChip/StatusChip';

interface SyncLogsListProps {
  title: string;
  logs: SyncLog[];
  loading?: boolean;
  emptyMessage?: string;
}

export const SyncLogsList = ({
  title,
  logs = [],
  loading = false,
  emptyMessage = 'No sync logs available',
}: SyncLogsListProps) => {
  const theme = useTheme();
  const { palette } = theme;
  const isLight = palette.mode === 'light';

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <NoDataInfo
        title={title}
        emptyMessage={emptyMessage}
        icon={<SyncAltOutlined fontSize="small" color="primary" />}
      />
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }} gutterBottom>
        <SyncAltOutlined fontSize="small" color="primary" />
        {title}
      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 1,
          boxShadow: '2px 2px 5px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          minHeight: 192,
          border: isLight ? `1px solid ${palette.grey[200]}` : 'none',
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                background: isLight ? palette.primary.lighter : palette.primary.light,
              }}
            >
              <TableCell sx={headerStyles}>Offer Name</TableCell>
              <TableCell sx={headerStyles}>Platform</TableCell>
              <TableCell sx={headerStyles}>Status</TableCell>
              <TableCell sx={headerStyles}>Sync Date</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.sync_log_id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={cellStyles}>{log.offer_name}</TableCell>
                <TableCell sx={cellStyles}>{log.scope}</TableCell>
                <TableCell sx={cellStyles}>
                  <StatusChip
                    label={log.sync_status === 'success' ? 'Success' : 'Error'}
                    status={log.sync_status === 'success' ? 'success' : 'error'}
                    useBoxStyle
                    tooltip={log.response_message || log.error_details || ''}
                  />
                </TableCell>
                <TableCell sx={cellStyles}>{format(new Date(log.created_at), 'MMM dd, yyyy HH:mm')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SyncLogsList;
